import { Fragment, useEffect, useMemo, useState } from 'react'

import { F0Box, F0Heading, F0Select, F0SelectItemObject, F0Text } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  WizardStep as DeprecatedWizardStep,
  WizardStepRenderParams as DeprecatedWizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import {
  NetsuitePurchaseOrderFieldMappingsDocument,
  NetsuitePurchaseOrderFieldMappingsQuery,
  NetsuitePurchaseOrdersStepDocument,
  ReplaceMappingsIntegrationsnetsuitePurchaseOrderFieldMappingMappingsInput,
  ReplaceNetsuitePurchaseOrderFieldMappingsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import {
  fieldLabel,
  sectionLabel,
} from 'modules/procurement/components/poTemplates/FieldEditor/translations'
import {
  SECTION_ORDER,
  toSectionType,
} from 'modules/procurement/components/poTemplates/FieldEditor/types'

import {
  NetsuitePurchaseOrderMapping,
  NetsuiteWizardData,
} from 'modules/integrations_netsuite/lib/types'

type NetSuiteWizardStep = DeprecatedWizardStep<NetsuiteWizardData>
type NetSuiteWizardStepRenderParams = DeprecatedWizardStepRenderParams<NetsuiteWizardData>

type FactorialField = {
  id: string
  label: string
  fieldKey?: string | null
  fieldType: string
  sectionType: string
  predefined: boolean
  visible: boolean
  computed: boolean
  position: number
}

type NetsuiteField = {
  id: string
  label: string
  fieldType: string
  sectionType: string
  custom: boolean
  required: boolean
  readOnly: boolean
}

type PersistedMappingEdge =
  NetsuitePurchaseOrderFieldMappingsQuery['integrationsNetsuite']['purchaseOrderFieldMappingsConnection']['edges'][number]

const SOURCE_KEY = 'purchase_orders'
const MAPPED_RESOURCE = 'procurement.purchase_order'

const factorialMappingKey = (field: Pick<FactorialField, 'id' | 'fieldKey'>) =>
  field.fieldKey ?? field.id

const indexMappingsByFactorialId = (mappings: NetsuitePurchaseOrderMapping[]) => {
  const index = new Map<string, NetsuitePurchaseOrderMapping>()
  for (const mapping of mappings) index.set(mapping.factorial_id, mapping)
  return index
}

const persistedMappings = (
  value: PersistedMappingEdge[] | undefined
): NetsuitePurchaseOrderMapping[] => {
  if (!value) return []
  const result: NetsuitePurchaseOrderMapping[] = []
  for (const { node } of value) {
    if (typeof node.mappedField !== 'string') continue
    result.push({
      factorial_id: node.fieldKey,
      netsuite_id: node.mappedField,
      meta: {
        factorial_section_type: node.sectionType,
        netsuite_section_type: node.sectionType,
        mapped_resource: node.mappedResource ?? '',
      },
    })
  }
  return result
}

const normalizeMappingKeys = (
  mappings: NetsuitePurchaseOrderMapping[],
  fields: FactorialField[]
): NetsuitePurchaseOrderMapping[] => {
  if (mappings.length === 0 || fields.length === 0) return mappings

  const byId = new Map(fields.map((field) => [field.id, field]))

  return mappings.map((mapping) => {
    const field = byId.get(mapping.factorial_id)
    if (!field) return mapping

    return {
      ...mapping,
      factorial_id: factorialMappingKey(field),
    }
  })
}

const filterMappingsByActiveFields = (
  mappings: NetsuitePurchaseOrderMapping[],
  fields: FactorialField[]
): NetsuitePurchaseOrderMapping[] => {
  if (fields.length === 0) return mappings

  const activeKeys = new Set(fields.map(factorialMappingKey))
  return mappings.filter((mapping) => activeKeys.has(mapping.factorial_id))
}

const mappingsPayload = (
  mappings: NetsuitePurchaseOrderMapping[]
): ReplaceMappingsIntegrationsnetsuitePurchaseOrderFieldMappingMappingsInput[] =>
  mappings.map((mapping) => ({
    fieldKey: mapping.factorial_id,
    sectionType: mapping.meta?.netsuite_section_type ?? 'body',
    mappedResource: MAPPED_RESOURCE,
    mappedField: mapping.netsuite_id,
  }))

const factorialFieldLabel = (field: FactorialField) => {
  if (field.predefined) return fieldLabel(field.label)

  return field.label
}

const factorialFieldDescription = (field: FactorialField) => field.fieldType

const netsuiteSectionTypeFor = (factorialField: FactorialField) =>
  toSectionType(factorialField.sectionType) === 'line_item_columns' ? 'line_item' : 'body'

const sectionPosition = (sectionType: string) => SECTION_ORDER.indexOf(toSectionType(sectionType))

const sortFactorialFields = (fields: FactorialField[]) =>
  [...fields].sort((a, b) => {
    const sectionDiff = sectionPosition(a.sectionType) - sectionPosition(b.sectionType)
    if (sectionDiff !== 0) return sectionDiff

    return a.position - b.position
  })

const groupFactorialFields = (fields: FactorialField[]) =>
  SECTION_ORDER.map((section) => ({
    section,
    fields: fields.filter((field) => toSectionType(field.sectionType) === section),
  })).filter(({ fields: sectionFields }) => sectionFields.length > 0)

const upsertMapping = ({
  mappings,
  factorialField,
  netsuiteField,
}: {
  mappings: NetsuitePurchaseOrderMapping[]
  factorialField: FactorialField
  netsuiteField?: NetsuiteField
}) => {
  const mappingKey = factorialMappingKey(factorialField)
  const withoutCurrent = mappings.filter(({ factorial_id }) => factorial_id !== mappingKey)
  if (!netsuiteField) return withoutCurrent

  return [
    ...withoutCurrent,
    {
      factorial_id: mappingKey,
      netsuite_id: netsuiteField.id,
      meta: {
        factorial_label: factorialFieldLabel(factorialField),
        factorial_field_key: factorialField.fieldKey ?? '',
        factorial_field_type: factorialField.fieldType,
        factorial_section_type: factorialField.sectionType,
        netsuite_label: netsuiteField.label,
        netsuite_field_type: netsuiteField.fieldType,
        netsuite_section_type: netsuiteField.sectionType,
        netsuite_custom: String(netsuiteField.custom),
      },
    },
  ]
}

const optionLabel = (field: NetsuiteField) => {
  const customSuffix = field.custom
    ? ` ${i18n.t('app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.custom_field_suffix')}`
    : ''

  return `${field.label} (${field.id})${customSuffix}`
}

const PurchaseOrdersStep = ({ data, onDataChanged }: NetSuiteWizardStepRenderParams) => {
  const session = useCurrentSession()
  const hasConnectionCredentials =
    !!data.answers.account_id && !!data.answers.client_id && !!data.answers.certificate_id

  const { data: queryData, loading } = useQuery(NetsuitePurchaseOrdersStepDocument, {
    variables: { companyId: session.company.id },
    skip: !hasConnectionCredentials,
    extra: { errorFlash: true },
  })
  const { data: mappingsData, loading: loadingMappings } = useQuery(
    NetsuitePurchaseOrderFieldMappingsDocument,
    {
      variables: { companyId: session.company.id },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
      extra: { errorFlash: true },
    }
  )
  const [replaceMappings] = useMutation(ReplaceNetsuitePurchaseOrderFieldMappingsDocument, {
    extractorFn: (responseData) =>
      responseData.integrationsNetsuiteMutations
        .replaceMappingsIntegrationsnetsuitePurchaseOrderFieldMapping,
    refetchQueries: [NetsuitePurchaseOrderFieldMappingsDocument],
    awaitRefetchQueries: true,
  })

  const poTemplate = queryData?.procurement.poTemplatesConnection.edges[0]?.node
  const activeVersion = poTemplate?.poTemplateVersionsConnection.edges[0]?.node

  const factorialFields = useMemo<FactorialField[]>(
    () =>
      activeVersion?.poTemplateFieldDefinitionsConnection.edges
        .map(({ node }) => node)
        .filter((field) => field.visible && !field.computed && field.fieldKey !== 'line_number') ??
      [],
    [activeVersion]
  )

  const groupedFactorialFields = useMemo(
    () => groupFactorialFields(sortFactorialFields(factorialFields)),
    [factorialFields]
  )

  const netsuiteFields = useMemo<NetsuiteField[]>(
    () =>
      queryData?.integrationsNetsuite.purchaseOrderFieldsConnection.edges.map(({ node }) => node) ??
      [],
    [queryData]
  )

  const netsuiteOptionsBySection = useMemo(() => {
    const bySection = new Map<string, F0SelectItemObject<string, NetsuiteField>[]>()
    for (const field of netsuiteFields) {
      const options = bySection.get(field.sectionType) ?? []
      options.push({ label: optionLabel(field), item: field, value: field.id })
      bySection.set(field.sectionType, options)
    }
    return bySection
  }, [netsuiteFields])

  const netsuiteFieldsById = useMemo(() => {
    const index = new Map<string, NetsuiteField>()
    for (const field of netsuiteFields) index.set(field.id, field)
    return index
  }, [netsuiteFields])

  const storedMappings = useMemo(
    () =>
      filterMappingsByActiveFields(
        normalizeMappingKeys(
          persistedMappings(
            mappingsData?.integrationsNetsuite.purchaseOrderFieldMappingsConnection.edges
          ),
          factorialFields
        ),
        factorialFields
      ),
    [mappingsData, factorialFields]
  )

  const [currentMappings, setCurrentMappings] = useState<NetsuitePurchaseOrderMapping[]>([])
  const [seeded, setSeeded] = useState(false)

  const currentMappingsById = useMemo(
    () => indexMappingsByFactorialId(currentMappings),
    [currentMappings]
  )

  useEffect(() => {
    if (seeded) return
    if (loadingMappings || loading) return
    setCurrentMappings(storedMappings)
    setSeeded(true)

    if (data.answers[SOURCE_KEY] !== undefined || data.sources?.[SOURCE_KEY] !== undefined) {
      const { [SOURCE_KEY]: _legacyAnswer, ...remainingAnswers } = data.answers
      const { [SOURCE_KEY]: _legacySource, ...remainingSources } = data.sources ?? {}
      onDataChanged({
        ...data,
        sources: remainingSources,
        answers: remainingAnswers,
      })
    }
  }, [seeded, loadingMappings, loading, storedMappings, data, onDataChanged])

  useOnNext(async () => {
    // Guard against wiping persisted mappings if the load query errored: without
    // a successful read we have no reliable baseline and `currentMappings` may
    // be empty (which would delete every stored mapping for the company).
    if (mappingsData === undefined) return Promise.reject()

    const result = await replaceMappings({
      variables: {
        companyId: session.company.id,
        mappings: mappingsPayload(currentMappings),
      },
    })
    const handled = handleResult(result, () => undefined)
    if (!handled.success) return Promise.reject()
  })

  const handleMappingChange = (factorialField: FactorialField, netsuiteId: string | null) => {
    const netsuiteField = netsuiteId ? netsuiteFieldsById.get(netsuiteId) : undefined
    setCurrentMappings((mappings) => upsertMapping({ mappings, factorialField, netsuiteField }))
  }

  if (loading || loadingMappings) {
    return <Spinner />
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="xl" maxWidth="screen-lg">
      <F0Box display="flex" flexDirection="column" gap="xs">
        <F0Heading
          as="h2"
          variant="heading"
          content={i18n.t(
            'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.title'
          )}
        />
        <F0Text
          variant="body"
          color="secondary"
          content={i18n.t(
            'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.description'
          )}
        />
      </F0Box>

      {!activeVersion ? (
        <F0Box padding="lg" border="default" borderColor="warning" borderRadius="md">
          <F0Text
            variant="body"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.no_template'
            )}
          />
        </F0Box>
      ) : null}

      {activeVersion && factorialFields.length === 0 ? (
        <F0Box padding="lg" border="default" borderColor="secondary" borderRadius="md">
          <F0Text
            variant="body"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.no_fields'
            )}
          />
        </F0Box>
      ) : null}

      {activeVersion && factorialFields.length > 0 ? (
        <F0Box border="default" borderColor="secondary" borderRadius="md" overflow="hidden">
          <F0Box display="flex" padding="md" background="secondary" gap="lg">
            <F0Box grow>
              <F0Text
                variant="label"
                content={i18n.t(
                  'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.factorial_fields'
                )}
              />
            </F0Box>
            <F0Box grow>
              <F0Text
                variant="label"
                content={i18n.t(
                  'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.netsuite_fields'
                )}
              />
            </F0Box>
          </F0Box>
          {groupedFactorialFields.map(({ section, fields }) => (
            <Fragment key={section}>
              <F0Box paddingX="md" paddingY="sm" background="secondary" borderTop="default">
                <F0Text variant="label" content={sectionLabel(section)} />
              </F0Box>
              {fields.map((field) => (
                <F0Box
                  key={field.id}
                  display="flex"
                  alignItems="center"
                  padding="md"
                  gap="lg"
                  borderTop="default"
                  borderColor="secondary"
                >
                  <F0Box grow display="flex" flexDirection="column" gap="xs">
                    <F0Text variant="body" content={factorialFieldLabel(field)} />
                    <F0Text
                      variant="description"
                      color="secondary"
                      content={factorialFieldDescription(field)}
                    />
                  </F0Box>
                  <F0Box grow>
                    <F0Select<string, NetsuiteField>
                      clearable
                      size="md"
                      options={netsuiteOptionsBySection.get(netsuiteSectionTypeFor(field)) ?? []}
                      value={
                        currentMappingsById.get(factorialMappingKey(field))?.netsuite_id ??
                        undefined
                      }
                      onChange={(value: string | null) => handleMappingChange(field, value)}
                      searchable
                      showSearchBox
                      label={factorialFieldLabel(field)}
                      hideLabel
                      placeholder={i18n.t(
                        'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.select_placeholder'
                      )}
                    />
                  </F0Box>
                </F0Box>
              ))}
            </Fragment>
          ))}
        </F0Box>
      ) : null}
    </F0Box>
  )
}

export const purchaseOrdersStep = (): NetSuiteWizardStep => ({
  title: i18n.t(
    'app_store_v2.app_integrations.integrations_netsuite.settings.steps.purchase_orders.wizard_title'
  ),
  nextText: i18n.t(
    'app_store_v2.app_integrations.integrations_netsuite.settings.steps.verify.finish_button'
  ),
  render: (props) => <PurchaseOrdersStep {...props} />,
  isCompleted: (_data) => true,
})
